import { UMR } from '../../urbanmap/_components';
import { PLAN } from '../proposition-c/tokens';
import type { Variants } from './types';

const grey = UMR.grey100;

function Block({ h, w = '100%', label }: { h: number; w?: number | string; label?: string }) {
  return (
    <div style={{ height: h, width: w, background: grey, border: `1px solid ${UMR.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: UMR.textSecondary }}>
      {label}
    </div>
  );
}

function Frame({ title, children, active }: { title: string; children: React.ReactNode; active?: boolean }) {
  return (
    <figure style={{ margin: 0, flex: 1, minWidth: 240, border: `2px solid ${active ? PLAN.accent : UMR.border}`, background: UMR.white }}>
      <figcaption style={{ padding: '6px 10px', fontSize: 12, fontWeight: 700, background: active ? PLAN.accentBg : UMR.grey100, color: active ? PLAN.accentStrong : UMR.textSecondary }}>
        {title}
      </figcaption>
      <div style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>{children}</div>
    </figure>
  );
}

function Switch({ label, value, onChange, options }: { label: string; value: string; onChange: (v: 'A' | 'B') => void; options: [string, string] }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
      <span style={{ minWidth: 200, color: UMR.textSecondary }}>{label}</span>
      {(['A', 'B'] as const).map((k, i) => (
        <button
          key={k}
          type="button"
          onClick={() => onChange(k)}
          style={{
            padding: '4px 10px', cursor: 'pointer', fontFamily: UMR.font, fontSize: 13,
            border: `1px solid ${value === k ? PLAN.accent : UMR.border}`,
            background: value === k ? PLAN.accentBg : UMR.white,
            color: value === k ? PLAN.accentStrong : UMR.text,
          }}
        >
          {k} · {options[i]}
        </button>
      ))}
    </div>
  );
}

/**
 * Low-fidelity view (`?view=wireframes`) — journey as numbered steps plus the variants
 * of every open UX hypothesis of `brief.md`. Shown before polishing high fidelity.
 */
export function Wireframes({ variants, onChange }: { variants: Variants; onChange: (v: Variants) => void }) {
  return (
    <div style={{ height: '100%', overflowY: 'auto', fontFamily: UMR.font, background: UMR.white }}>
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: 24, display: 'flex', flexDirection: 'column', gap: 24 }}>
        <header>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Parcours et variantes — revue basse fidélité</h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: UMR.textSecondary }}>
            Trois choix UX ne sont pas tranchés par le PRD. Choisissez une variante par sujet : la haute fidélité s’y adapte.
          </p>
        </header>

        <section style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 14, border: `1px solid ${UMR.border}` }}>
          <Switch label="Forme de la saisie" value={variants.form} options={['Assistant 3 étapes', 'Dialogue unique']} onChange={(form) => onChange({ ...variants, form })} />
          <Switch label="Moment de l’invitation" value={variants.invite} options={['Pendant la création', 'Après création']} onChange={(invite) => onChange({ ...variants, invite })} />
          <Switch label="Impact des hypothèses" value={variants.impact} options={['Comparatif avant/après', 'Simple mention']} onChange={(impact) => onChange({ ...variants, impact })} />
        </section>

        <section>
          <h2 style={{ fontSize: 16, margin: '0 0 10px' }}>Parcours</h2>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <Frame title="1 · UrbanMap — périmètre sélectionné">
              <Block h={90} label="Carte" />
              <Block h={44} label="Carte Plan + « Créer un projet Plan »" />
            </Frame>
            <Frame title="2 · Périmètre">
              <Block h={20} label="Nom du projet" />
              <Block h={20} label="Type de territoire" />
              <Block h={20} label="Territoire" />
              <Block h={54} label="Repris depuis UrbanMap" />
            </Frame>
            <Frame title="3 · Hypothèses de l’AO">
              <Block h={20} label="Station + année météo" />
              <Block h={20} label="Ratios résidentiel / tertiaire / sans DPE" />
              <Block h={54} label={variants.impact === 'A' ? 'Comparatif UM → projet' : 'Mention « recalculées »'} />
            </Frame>
            <Frame title={variants.invite === 'A' ? '4 · Équipe (dans la création)' : '4 · Projet créé'} active>
              <Block h={30} label={variants.invite === 'A' ? 'Membres invités' : 'En-tête + bouton Inviter'} />
              <Block h={84} label="Carte du projet + fiche bâtiment" />
            </Frame>
            <Frame title="5 · Mes projets">
              <Block h={26} label="Projet 1 — territoire, date, membres" />
              <Block h={26} label="Projet 2" />
              <Block h={26} label="Projet 3" />
            </Frame>
          </div>
        </section>

        <section>
          <h2 style={{ fontSize: 16, margin: '0 0 10px' }}>Variantes côte à côte</h2>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <Frame title="A · Assistant 3 étapes" active={variants.form === 'A'}>
              <Block h={18} label="1 Périmètre › 2 Hypothèses › 3 Équipe" />
              <Block h={96} label="Une étape à la fois" />
              <Block h={18} label="Précédent · Suivant" />
            </Frame>
            <Frame title="B · Dialogue unique à sections" active={variants.form === 'B'}>
              <Block h={34} label="1 Périmètre (déplié)" />
              <Block h={34} label="2 Hypothèses (déplié)" />
              <Block h={24} label="3 Équipe (replié)" />
              <Block h={18} label="Créer le projet" />
            </Frame>
            <Frame title="A · Comparatif avant/après" active={variants.impact === 'A'}>
              <Block h={30} label="UrbanMap 42 100 → Projet 38 900 MWh/an" />
              <Block h={20} label="Écart −7,6 % · normal" />
            </Frame>
            <Frame title="B · Simple mention" active={variants.impact === 'B'}>
              <Block h={50} label="« Les consommations seront recalculées »" />
            </Frame>
          </div>
        </section>
      </div>
    </div>
  );
}
